/**
 * LearnerCourseDetailLayout
 *
 * Learner-facing course detail experience. A single page with a
 * consistent hero across the top — title, instructor, and a media
 * slot that plays the admin-configured sample video when set or
 * falls back to the course thumbnail — and three tabs below:
 *
 *   1. CE Information      — accreditation block authored on the
 *                            course edit page (AGD subject code,
 *                            PACE provider, statements).
 *   2. About the Instructor — bio + portrait from the instructor's
 *                             reusable template.
 *   3. Course Overview     — curriculum / lesson list.
 *
 * Data layer comes from the surrounding page (CourseDetail.tsx);
 * this component is purely the presentation skeleton + tab routing.
 */

import { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  LinearProgress,
  Tab,
  Tabs,
  Typography,
} from '../ui';
import { PlayCircleIcon, SchoolIcon } from '../icons';
import { Course, CourseDetailTab, Lesson, Module } from '../../apis/api-client';
import { CourseAuthor } from '../../apis/api-client';
import { useSelector } from 'react-redux';
import { RootState } from '../../types';
import { selectAuthorById, useEnsureAuthors } from '../../features/lookups/lookupsSlice';
import { getPortraitObjectPosition } from '../../utils/courseAuthor';
import { getCEInfo, hasCEInfo } from '../../utils/courseCEInfo';
import CourseInstructorTab from './CourseInstructorTab';
import CourseCETab from './CourseCETab';
import CourseOverviewTab from './CourseOverviewTab';
import TiptapViewer from '../common/TiptapViewer';
import {
  getSampleVideoEnabled,
  getSampleVideoUrl,
} from '../../utils/coursePrefs';

// Native structured tabs plus one entry per imported/authored HTML detail
// tab (`detail-<id>`). Detail tabs carry legacy one-off HTML blocks — CE
// info, instructor cards — that don't map to the structured sections.
type LearnerTab = 'ce' | 'instructor' | 'overview' | `detail-${number}`;

export interface LearnerCourseDetailLayoutProps {
  course: Course;
  thumbnailDisplayUrl?: string | null;
  /** Total lessons in the course. */
  lessonCount?: number;
  /** Completed lesson count for "in progress" learners. */
  completedCount?: number;
  /** Modules (sections) for the curriculum-at-a-glance list shown on
   *  the Course Overview tab. */
  modules?: Module[];
  /** Full lesson list, used to count lessons per module and surface
   *  the lessons that aren't bucketed into any module. */
  lessons?: Lesson[];
  /** Label for the curriculum CTA. "Start course", "Continue", or
   *  "Review" depending on enrollment state. */
  ctaLabel?: string;
  /** Fires when the learner clicks the Course Overview CTA. The parent
   *  flips its existing TabNavigation to the lessons tab so the
   *  existing player flow takes over. */
  onOpenCurriculum?: () => void;
  /** Enrollment state for the page-level Enroll button at the top
   *  right of the hero. When omitted, the button is hidden. */
  isEnrolled?: boolean;
  /** Fires when the learner clicks the top-right Enroll button.
   *  Omit to hide the button entirely (e.g. on draft courses). */
  onEnroll?: () => void;
  /** When true, the Enroll button shows a spinner and is disabled
   *  while the request is in flight. */
  enrolling?: boolean;
  /** When the viewer can edit the course (admins), pass the path to
   *  the course settings editor so the empty-instructor states can
   *  render a fast-track "Assign instructor" link rather than a dead
   *  end. Omit for plain learners — the CTA is hidden. */
  editLink?: string;
}

export default function LearnerCourseDetailLayout({
  course,
  thumbnailDisplayUrl,
  lessonCount,
  completedCount,
  ctaLabel = 'Open course',
  onOpenCurriculum,
  isEnrolled,
  onEnroll,
  enrolling = false,
  modules,
  lessons,
  editLink,
}: LearnerCourseDetailLayoutProps) {
  useEnsureAuthors();
  const author: CourseAuthor | null =
    useSelector((state: RootState) => selectAuthorById(state, course?.authorId)) ?? null;
  const ceInfo = useMemo(
    () => getCEInfo(course?.ceInfo),
    [course?.ceInfo],
  );
  // Admin visibility toggles — feature data may exist but be hidden
  // from the learner. Both default true and live on the course record.
  const sampleVideoEnabled = getSampleVideoEnabled(course);
  const ceInfoEnabled = course?.ceInfoEnabled ?? true;
  const sampleVideoUrl = sampleVideoEnabled ? getSampleVideoUrl(course) : '';
  const ceHours = course?.ceHours;
  const ceTabHasContent = useMemo(
    () => ceInfoEnabled && (hasCEInfo(course?.ceInfo) || ceHours != null),
    [course?.ceInfo, ceHours, ceInfoEnabled],
  );
  // Structured CE + instructor tabs surface only when they hold real
  // content. Legacy/migrated courses (no structured CE, no assigned
  // author) keep them hidden and carry that content in their imported
  // HTML detail tabs instead. Empty tabs never render.
  const showCETab = ceTabHasContent;
  const showInstructorTab = author != null;

  // One-off HTML blocks (CE / instructor cards from imports, or admin-authored
  // marketing tabs) rendered in the strip alongside the structured tabs. HTML
  // goes through TiptapViewer, which sandboxes author `<style>` so it can't
  // bleed into the rest of the app.
  const detailTabs = useMemo(
    () =>
      ((course?.detailTabs as CourseDetailTab[] | undefined) ?? [])
        .filter((t) => !t.isDeleted && (t.contentHtml ?? '').trim() !== '')
        .sort((a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0)),
    [course?.detailTabs],
  );

  const [tab, setTab] = useState<LearnerTab>(ceTabHasContent ? 'ce' : 'overview');

  // Selected tab may point at a section that isn't currently visible (e.g. the
  // default 'ce' when CE is hidden, or a detail tab that was removed) — fall
  // back to Course Overview, which is always present.
  const visibleTabs: LearnerTab[] = [
    ...(showCETab ? (['ce'] as const) : []),
    ...(showInstructorTab ? (['instructor'] as const) : []),
    ...detailTabs.map((t) => `detail-${t.id}` as LearnerTab),
    'overview',
  ];
  const activeTab: LearnerTab = visibleTabs.includes(tab) ? tab : 'overview';

  // Top-band CTA — replaces both the page-level blue header banner
  // (retired in CourseDetail.tsx) and the Enroll button that used to
  // sit beside the tab strip. State-aware label + handler:
  //   Not enrolled → "Enroll" → onEnroll
  //   Enrolled / completed → "Continue learning" / "Review lessons" → onOpenCurriculum
  const topHandler = !isEnrolled ? onEnroll : onOpenCurriculum;
  const topLabel = !isEnrolled
    ? enrolling
      ? 'Enrolling…'
      : 'Enroll'
    : ctaLabel;
  const TopIcon = !isEnrolled ? SchoolIcon : PlayCircleIcon;

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Title band — the course title is the page's primary heading
          here (no more blue banner above) and the page-level CTA
          (Enroll / Continue / Review) pins to the right. Wraps onto
          its own row on mobile so the title can breathe. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          mb: 3,
        }}
      >
        <Typography
          variant="h3"
          sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}
        >
          {course?.title}
        </Typography>
        {topHandler && (
          <Button
            variant="contained"
            color="primary"
            onClick={topHandler}
            disabled={!isEnrolled && enrolling}
            startIcon={
              !isEnrolled && enrolling ? undefined : <TopIcon />
            }
            sx={{
              color: '#fff',
              textTransform: 'none',
              fontWeight: 700,
              minWidth: 160,
              flexShrink: 0,
              '&.Mui-disabled': { color: '#fff', opacity: 0.7 },
            }}
          >
            {topLabel}
          </Button>
        )}
      </Box>

      {/* Hero — media on the left, course description + instructor
          on the right. Title moved up to the band above; this column
          leads with a "Course Description" label so the descriptive
          text is clearly labeled instead of looking like a stray
          subtitle. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' },
          gap: { xs: 2.5, md: 4 },
          // `start` anchors the title to the top of the media so the
          // description (now in this column) can grow without
          // visually de-centering the band.
          alignItems: 'start',
          mb: 3,
        }}
      >
        <HeroMedia
          sampleVideoUrl={sampleVideoUrl}
          thumbnailUrl={thumbnailDisplayUrl}
          title={course?.title || ''}
        />
        <Box sx={{ minWidth: 0 }}>
          {/* Course Description — eyebrow label + body copy. Replaces
              the duplicated title that used to live here. */}
          {course?.description && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box
                  sx={{
                    width: 24,
                    height: 2,
                    bgcolor: 'primary.main',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="overline"
                  sx={{
                    color: 'primary.main',
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                  }}
                >
                  Course Description
                </Typography>
                {/* Matching cyan dash on the right so the eyebrow
                    label sits balanced between two accent bars. */}
                <Box
                  sx={{
                    width: 24,
                    height: 2,
                    bgcolor: 'primary.main',
                    flexShrink: 0,
                  }}
                />
              </Box>
              <Typography
                variant="body1"
                color="text.primary"
                sx={{
                  mb: 2,
                  whiteSpace: 'pre-line',
                  wordBreak: 'break-word',
                  lineHeight: 1.6,
                }}
              >
                {course.description}
              </Typography>
            </>
          )}
          {author && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                src={author.portraitUrl || undefined}
                imgProps={{ style: { objectPosition: getPortraitObjectPosition(author) } }}
                sx={{ width: 48, height: 48 }}
              >
                {author.name.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="overline" sx={{ display: 'block', lineHeight: 1 }}>
                  Instructed by
                </Typography>
                <Typography variant="subtitle1" fontWeight={700} noWrap>
                  {author.name}
                </Typography>
                {/* Credential / title sits underneath the name as
                    primary-cyan text — feels lighter than the previous
                    pill chip, which read like a status badge. The
                    blue is the same `primary.main` we use elsewhere
                    for the brand-anchored accents. */}
                {author.credentials && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: 'primary.main',
                      fontWeight: 600,
                      lineHeight: 1.25,
                      mt: 0.25,
                    }}
                  >
                    {author.credentials}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          {/* Learner view of a course with no instructor: render nothing.
              The empty space sits more cleanly than a "not assigned"
              status line that the learner can't do anything about.
              Admins still get the amber warning band below since they
              have an actionable next step. */}
          {!author && editLink && (
            // Admin-only warning band. Amber so it reads as "action
            // required" rather than a quiet status line — the previous
            // muted version was easy to miss when admins thought they
            // had saved the instructor.
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.25,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'rgba(245,124,0,0.35)',
                bgcolor: 'warning.softBg',
                flexWrap: 'wrap',
              }}
            >
              <SchoolIcon sx={{ fontSize: 18, color: 'warning.softText', flexShrink: 0 }} />
              <Typography
                variant="body2"
                sx={{ color: 'warning.softText', flex: 1, minWidth: 0 }}
              >
                <strong>Instructor not assigned.</strong> Learners won't
                see a bio or photo until you pick one in settings.
              </Typography>
              <Button
                size="small"
                variant="contained"
                href={editLink}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  bgcolor: 'warning.softText',
                  color: '#fff',
                  flexShrink: 0,
                  '&:hover': { bgcolor: '#5c4400' },
                }}
              >
                Assign instructor →
              </Button>
            </Box>
          )}

          {/* Progress band — slotted into the right column under the
              instructor row so the meter sits inside the same visual
              card as the rest of the course's identifying info, level
              with the hero thumbnail on the left. Renders only for
              enrolled learners with a real lesson count; anonymous
              viewers see nothing here. The numbers come straight from
              the parent (which sums local completedLessonIds against
              the lesson list) so they update the moment a lesson is
              marked complete. */}
          {isEnrolled && (lessonCount ?? 0) > 0 && (() => {
            const total = lessonCount ?? 0;
            const done = Math.min(completedCount ?? 0, total);
            const pct = total > 0 ? Math.round((done / total) * 100) : 0;
            return (
              <Box sx={{ mt: 2.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    mb: 0.75,
                    gap: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      fontWeight: 700,
                      color: 'primary.main',
                    }}
                  >
                    Your progress
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      {done} of {total} lesson{total === 1 ? '' : 's'} complete
                    </Typography>
                    <Typography variant="body2" fontWeight={700} color="primary.main">
                      {pct}%
                    </Typography>
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Math.max(0, pct))}
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    bgcolor: 'grey.100',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 999,
                      background:
                        'var(--brand-gradient)',
                    },
                  }}
                />
              </Box>
            );
          })()}
        </Box>
      </Box>

      {/* Tab strip — CE Information tab is gated on the admin
          visibility toggle. When hidden, the strip drops to two tabs
          and any pre-selected `ce` state slides to Course Overview.
          Enroll CTA sits on the right of this row so it shares
          horizontal real estate with the tabs rather than floating
          above the hero. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 2,
          borderBottom: 1,
          borderColor: 'divider',
          mb: 2,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_e, v) => setTab(v)}
          sx={{ flex: 1, minWidth: 0 }}
        >
          {showCETab && <Tab value="ce" label="CE Information" />}
          {showInstructorTab && <Tab value="instructor" label="About the Instructor" />}
          {detailTabs.map((t) => (
            <Tab key={t.id} value={`detail-${t.id}`} label={t.title || 'Details'} />
          ))}
          <Tab value="overview" label="Course Overview" />
        </Tabs>
        {/* Enroll / Continue / Review CTA moved up to the page's top
            title band — the tab strip stays focused on tab navigation. */}
      </Box>

      {/* Tab bodies */}
      {showCETab && activeTab === 'ce' && (
        <CourseCETab ceInfo={ceInfo} ceHours={ceHours} hasContent={ceTabHasContent} />
      )}

      {showInstructorTab && activeTab === 'instructor' && (
        <CourseInstructorTab author={author} editLink={editLink} />
      )}

      {detailTabs.map(
        (t) =>
          activeTab === `detail-${t.id}` && (
            <Card key={t.id} sx={{ p: { xs: 2, md: 3 } }}>
              <TiptapViewer content={t.contentHtml || ''} />
            </Card>
          ),
      )}

      {activeTab === 'overview' && <CourseOverviewTab modules={modules} lessons={lessons} />}
    </Box>
  );
}

// ── Hero media ───────────────────────────────────────────────────────

interface HeroMediaProps {
  sampleVideoUrl: string;
  thumbnailUrl?: string | null;
  title: string;
}

function HeroMedia({ sampleVideoUrl, thumbnailUrl, title }: HeroMediaProps) {
  const ytMatch = sampleVideoUrl.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{6,})/);
  const vimeoMatch = sampleVideoUrl.match(/vimeo\.com\/(\d+)/);
  const isYouTube = !!ytMatch;
  const isVimeo = !!vimeoMatch;
  const isDirectVideo = !!sampleVideoUrl && !isYouTube && !isVimeo;

  const wrapperSx = {
    position: 'relative' as const,
    width: '100%',
    // Slightly shorter than 16:9 (~11% less height at the same width)
    // so the hero band sits as a banner crop rather than a full video
    // frame. Mirrors the aspect ratio we use on catalog cards so the
    // course thumbnail reads the same on both surfaces.
    aspectRatio: '2 / 1',
    overflow: 'hidden',
    borderRadius: 2,
    bgcolor: 'grey.100',
  };

  if (isYouTube) {
    const id = ytMatch![1];
    return (
      <Box sx={wrapperSx}>
        <Box
          component="iframe"
          src={`https://www.youtube.com/embed/${id}`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          sx={{ width: '100%', height: '100%', border: 0 }}
        />
      </Box>
    );
  }
  if (isVimeo) {
    const id = vimeoMatch![1];
    return (
      <Box sx={wrapperSx}>
        <Box
          component="iframe"
          src={`https://player.vimeo.com/video/${id}`}
          title={title}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          sx={{ width: '100%', height: '100%', border: 0 }}
        />
      </Box>
    );
  }
  if (isDirectVideo) {
    return (
      <Box sx={wrapperSx}>
        <Box
          component="video"
          src={sampleVideoUrl}
          controls
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </Box>
    );
  }
  // No sample video — fall back to the course thumbnail.
  return (
    <Box sx={wrapperSx}>
      {thumbnailUrl ? (
        <Box
          component="img"
          src={thumbnailUrl}
          alt={title}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
          <SchoolIcon sx={{ fontSize: 64, opacity: 0.4 }} />
        </Box>
      )}
    </Box>
  );
}
